import { Button, Space, Tag, Tooltip } from "antd";
import { FilterFilled } from "@ant-design/icons";
import { ReactComponent as EditDocumentIcon } from "../../../assets/icon/events/editDocumentIcon.svg";
import { ReactComponent as SearchCircleIcon } from "../../../assets/icon/events/searchCircleIcon.svg";
import { ReactComponent as TrashIcon } from "../../../assets/icon/events/trashIcon.svg";
import { EVENT_STATUS_CONFIG, EVENT_TYPE_CONFIG } from "./constants";
import {
  buildFilters,
  getDateTimestamp,
  getNumericValue,
} from "./utils/eventFormatters";
import classes from "./Events.module.scss";

const filterIcon = <FilterFilled className={classes.FilterIcon} />;

const buildStatusFilters = () =>
  Object.entries(EVENT_STATUS_CONFIG).map(([value, config]) => ({
    text: config.label,
    value,
  }));

const getResponsiveCellProps = (label) => () => ({
  "data-label": label,
});

export const getEventColumns = ({
  events,
  deletingEventId,
  onDeleteEvent,
  onEditEvent,
  onViewEvent,
}) => [
  {
    title: "",
    dataIndex: "displayId",
    key: "displayId",
    align: "center",
    width: "5%",
    onCell: getResponsiveCellProps("ID"),
    render: (value) => <span className={classes.IdCell}>{value}</span>,
  },
  {
    title: "Evento",
    dataIndex: "name",
    key: "name",
    width: "24%",
    filters: buildFilters(events, "name"),
    filterIcon,
    filterSearch: true,
    onCell: getResponsiveCellProps("Evento"),
    onFilter: (value, record) => record.name === value,
    sorter: (a, b) => a.name.localeCompare(b.name),
  },
  {
    title: "Status",
    dataIndex: "status",
    key: "status",
    width: "12%",
    filters: buildStatusFilters(),
    filterIcon,
    onCell: getResponsiveCellProps("Status"),
    onFilter: (value, record) => record.status === value,
    render: (status) => {
      const config = EVENT_STATUS_CONFIG[status] || EVENT_STATUS_CONFIG.soon;

      return (
        <Tag className={classes.StatusTag} style={config}>
          {config.label}
        </Tag>
      );
    },
  },
  {
    title: "Data início",
    dataIndex: "startDate",
    key: "startDate",
    width: "13%",
    filters: buildFilters(events, "startDate"),
    filterIcon,
    onCell: getResponsiveCellProps("Data início"),
    onFilter: (value, record) => record.startDate === value,
    sorter: (a, b) =>
      getDateTimestamp(a.startDate) - getDateTimestamp(b.startDate),
  },
  {
    title: "Data fim",
    dataIndex: "endDate",
    key: "endDate",
    width: "13%",
    filters: buildFilters(events, "endDate"),
    filterIcon,
    onCell: getResponsiveCellProps("Data fim"),
    onFilter: (value, record) => record.endDate === value,
    sorter: (a, b) => getDateTimestamp(a.endDate) - getDateTimestamp(b.endDate),
  },
  {
    title: "Tipo",
    dataIndex: "category",
    key: "category",
    width: "11%",
    filters: buildFilters(events, "category"),
    filterIcon,
    onCell: getResponsiveCellProps("Tipo"),
    onFilter: (value, record) => record.category === value,
    render: (category) => (
      <Tag
        className={classes.TypeTag}
        style={EVENT_TYPE_CONFIG[category] || EVENT_TYPE_CONFIG.Ranking}
      >
        {category}
      </Tag>
    ),
  },
  {
    title: "Participantes",
    dataIndex: "participants",
    key: "participants",
    width: "12%",
    filters: buildFilters(events, "participants"),
    filterIcon,
    onCell: getResponsiveCellProps("Participantes"),
    onFilter: (value, record) => record.participants === value,
    sorter: (a, b) =>
      getNumericValue(a.participants) - getNumericValue(b.participants),
  },
  {
    title: "Ações",
    key: "actions",
    align: "center",
    width: "10%",
    onCell: getResponsiveCellProps("Ações"),
    render: (_, record) => (
      <Space size={6} className={classes.ActionButtons}>
        <Tooltip title="Visualizar evento">
          <Button
            type="text"
            aria-label={`Visualizar ${record.name}`}
            className={`${classes.IconButton} ${classes.ViewButton}`}
            icon={<SearchCircleIcon />}
            onClick={() => onViewEvent(record)}
          />
        </Tooltip>
        <Tooltip title="Editar evento">
          <Button
            type="text"
            aria-label={`Editar ${record.name}`}
            className={`${classes.IconButton} ${classes.EditButton}`}
            icon={<EditDocumentIcon />}
            onClick={() => onEditEvent(record)}
          />
        </Tooltip>
        <Tooltip title="Excluir evento">
          <Button
            type="text"
            aria-label={`Excluir ${record.name}`}
            className={`${classes.IconButton} ${classes.DeleteButton}`}
            icon={<TrashIcon />}
            loading={deletingEventId === record.id}
            onClick={() => onDeleteEvent(record)}
          />
        </Tooltip>
      </Space>
    ),
  },
];
